import React, { useCallback, useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/** Convert a relative `/api/files/x.png` into an absolute, browser-loadable URL. */
export function resolveImageUrl(u) {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://") || u.startsWith("data:")) return u;
  return `${BACKEND_URL}${u}`;
}

// Accepted MIME types must stay in sync with core/image_upload.py on the
// backend. Note the file input's `accept` attribute is a UX hint only —
// desktop file pickers filter by extension while iOS/Android use MIME.
// Real validation happens server-side via magic-byte sniffing, so this
// list is intentionally permissive (client-side checks are convenience).
const ACCEPTED_MIME = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
];
// Include extensions explicitly for Safari on macOS/iOS — some versions
// return an empty `type` for HEIC files, so we also whitelist by suffix.
const ACCEPTED_EXTS = [".jpg", ".jpeg", ".png", ".webp", ".gif", ".heic", ".heif"];
const ACCEPTED = [...ACCEPTED_MIME, ...ACCEPTED_EXTS].join(",");

// Friendly error text — must exactly match backend FRIENDLY_ERROR so the
// user sees the same message whether validation trips client- or server-side.
const FRIENDLY_ERROR =
  "Only image files are allowed. Supported formats: JPG, JPEG, PNG, " +
  "WebP, HEIC (iPhone), HEIF, and GIF. Please upload a valid image.";

const DEFAULT_MAX_BYTES = 8 * 1024 * 1024; // 8 MB — the absolute inbound cap on the backend.

/**
 * <ImageUploader /> — drag-and-drop + click-to-pick image uploader.
 *
 * Props:
 *   value             string | string[]   - current image URL or list of URLs (controlled)
 *   onChange          (next) => void      - called with new URL(s)
 *   multiple          boolean             - allow multiple images (gallery mode)
 *   maxFiles          number              - cap for multiple mode (default 20)
 *   maxBytesPerImage  number              - per-image size cap in bytes. When
 *                                          provided (e.g. 1 MB on Premium) it
 *                                          overrides the 8 MB default so the
 *                                          client rejects oversize files
 *                                          BEFORE hitting the network.
 *   aspectRatio       "square" | "wide"   - preview shape
 *   testId            string              - data-testid prefix
 *   className         string
 *   coverUrl          string              - (multiple only) URL currently marked as cover
 *   onSetCover        (url) => void       - (multiple only) called when user picks a new cover
 */
export default function ImageUploader({
  value,
  onChange,
  multiple = false,
  maxFiles = 20,
  maxBytesPerImage,
  aspectRatio = "wide",
  testId = "image-uploader",
  className = "",
  coverUrl,
  onSetCover,
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const perImageCap = maxBytesPerImage && maxBytesPerImage > 0 ? maxBytesPerImage : DEFAULT_MAX_BYTES;
  const perImageMb = perImageCap / (1024 * 1024);

  const items = multiple
    ? (Array.isArray(value) ? value : [])
    : (value ? [value] : []);

  const uploadOne = useCallback(async (file) => {
    if (!file) return null;
    if (file.size > perImageCap) {
      const mbLabel = perImageMb < 1 ? `${(perImageMb * 1024).toFixed(0)} KB` : `${perImageMb.toFixed(1).replace(/\.0$/, "")} MB`;
      toast.error(`"${file.name}" is too large. Max ${mbLabel} per image on your current plan.`);
      return null;
    }
    // Client-side MIME/extension check. iOS Safari sometimes returns an
    // empty `file.type` for HEIC captures, so we accept a match on either
    // the MIME type OR the file extension. Server-side magic-byte check
    // is the authoritative gate — this is UX-only.
    const nameLower = (file.name || "").toLowerCase();
    const mimeOk = ACCEPTED_MIME.includes((file.type || "").toLowerCase());
    const extOk = ACCEPTED_EXTS.some((e) => nameLower.endsWith(e));
    if (!mimeOk && !extOk) {
      toast.error(FRIENDLY_ERROR);
      return null;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Backend returns { deduped: true } when the same owner uploaded an
      // identical file before — surface a subtle info toast so users know
      // we reused their existing image (helps explain "I uploaded but the
      // count didn't change").
      if (r.data?.deduped) {
        toast.info(`"${file.name}" matches an existing image — reused instead of duplicated.`);
      }
      return r.data.url;
    } catch (err) {
      const msg = err.response?.data?.detail || FRIENDLY_ERROR;
      toast.error(msg);
      return null;
    }
  }, [perImageCap, perImageMb]);

  const handleFiles = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      const files = Array.from(fileList);
      if (!multiple) {
        const url = await uploadOne(files[0]);
        if (url) onChange(url);
      } else {
        const remaining = Math.max(0, maxFiles - items.length);
        if (remaining === 0) {
          toast.error(`You've reached the limit of ${maxFiles} images.`);
          return;
        }
        const toUpload = files.slice(0, remaining);
        const uploaded = [];
        for (const f of toUpload) {
          const url = await uploadOne(f);
          if (url) uploaded.push(url);
        }
        if (uploaded.length) {
          onChange([...items, ...uploaded]);
          toast.success(`${uploaded.length} image${uploaded.length === 1 ? "" : "s"} uploaded`);
        }
        if (files.length > remaining) {
          toast.info(`Only the first ${remaining} were uploaded (limit ${maxFiles}).`);
        }
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [multiple, maxFiles, items, onChange, uploadOne]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const removeAt = useCallback((idx) => {
    if (multiple) {
      const next = items.filter((_, i) => i !== idx);
      onChange(next);
    } else {
      onChange("");
    }
  }, [multiple, items, onChange]);

  const ratioCls = aspectRatio === "square" ? "aspect-square" : "aspect-[4/3]";

  return (
    <div className={className} data-testid={testId}>
      {/* Drop / pick zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileRef.current?.click(); }}
        data-testid={`${testId}-dropzone`}
        className={`relative rounded-2xl border-2 border-dashed transition-colors cursor-pointer p-6 text-center
          ${dragOver ? "border-[#C9A86A] bg-[#F8F1E4]" : "border-[#E7DDCF] bg-[#F5EFE6]/60 hover:bg-[#F5EFE6]"}
        `}
      >
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPTED}
          multiple={multiple}
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          data-testid={`${testId}-input`}
        />
        <div className="flex flex-col items-center gap-2">
          {uploading ? (
            <Loader2 size={26} className="text-[#1F4E3D] animate-spin" />
          ) : (
            <div className="w-11 h-11 rounded-full bg-white border border-[#E7DDCF] flex items-center justify-center">
              <Upload size={18} className="text-[#1F4E3D]" />
            </div>
          )}
          <div className="text-sm font-medium text-[#111827]">
            {uploading ? "Uploading…" : (multiple ? "Add images" : (items.length ? "Replace image" : "Upload image"))}
          </div>
          <div className="text-xs text-[#4B5563]">
            Drag &amp; drop, or click to browse. JPG, PNG, WebP, HEIC (iPhone), HEIF, or GIF. Auto-resized to fit ≤&nbsp;1&nbsp;MB.
            {multiple && ` Up to ${maxFiles} images.`}
          </div>
        </div>
      </div>

      {/* Previews */}
      {items.length > 0 && (
        <div className={`mt-3 grid gap-2 ${multiple ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-1 sm:max-w-xs"}`} data-testid={`${testId}-previews`}>
          {items.map((u, i) => {
            // In gallery mode, an image is "the cover" when its URL matches coverUrl.
            // Falls back to "first item" semantics when no coverUrl is provided
            // (e.g. older callers that haven't wired up onSetCover yet).
            const isCover = multiple && (coverUrl ? u === coverUrl : i === 0);
            return (
              <div
                key={`${u}-${i}`}
                className={`relative group rounded-xl overflow-hidden bg-[#F5EFE6] ${ratioCls} border-2 transition-colors ${isCover ? "border-[#C9A86A] ring-2 ring-[#C9A86A]/30" : "border-[#E7DDCF]"}`}
                data-testid={`${testId}-item-${i}`}
              >
                <img
                  src={resolveImageUrl(u)}
                  alt={`upload ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />

                {/* Top-right: remove */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                  className="absolute top-1.5 right-1.5 p-1 rounded-full bg-white shadow-sm hover:bg-red-50 text-[#B42318] opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove image"
                  data-testid={`${testId}-remove-${i}`}
                >
                  <X size={12} />
                </button>

                {/* Top-left: set-as-cover (only in gallery mode, only when not already cover) */}
                {multiple && onSetCover && !isCover && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onSetCover(u); }}
                    className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-white/95 hover:bg-white shadow-sm text-[#7A5A22] text-[10px] font-medium opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                    aria-label="Set as cover image"
                    title="Set as cover image"
                    data-testid={`${testId}-set-cover-${i}`}
                  >
                    <Star size={10} /> Set cover
                  </button>
                )}

                {/* Bottom-left: cover badge */}
                {isCover && (
                  <span
                    className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[#C9A86A] text-[#111827] text-[10px] font-medium shadow-sm"
                    data-testid={`${testId}-cover-badge-${i}`}
                  >
                    <Star size={10} fill="currentColor" /> Cover
                  </span>
                )}
              </div>
            );
          })}
          {multiple && items.length === 0 && (
            <div className={`rounded-xl bg-[#F5EFE6] ${ratioCls} flex items-center justify-center text-[#4B5563]`}>
              <ImageIcon size={20} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
